import { useState, useCallback, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { NFC } from '@exxili/capacitor-nfc';
import { isCapacitor } from '@/lib/config';
import logger from '@/lib/logger';

interface NFCReadResult {
  serialNumber: string;
  data: any;
  recordType: string;
  timestamp: number;
}

interface NFCWritePayload {
  tagType?: string;
  workOrderId?: string;
  processId?: string;
  moldId?: string;
  tagUid?: string;
  plotterCutId?: string;
  customData?: Record<string, any>;
}

interface NFCScanResult {
  serialNumber: string;
  isEmpty: boolean;
}

interface UseNFCReaderReturn {
  isScanning: boolean;
  isWriting: boolean;
  isSupported: boolean;
  lastRead: NFCReadResult | null;
  startScanning: () => Promise<void>;
  stopScanning: () => void;
  writeToTag: (data: NFCWritePayload) => Promise<void>;
  scanTagUid: () => Promise<NFCScanResult | null>;
  writeFullTag: (uid: string, data: NFCWritePayload) => Promise<boolean>;
}

export const useNFCReader = (): UseNFCReaderReturn => {
  const [isScanning, setIsScanning] = useState(false);
  const [isWriting, setIsWriting] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [lastRead, setLastRead] = useState<NFCReadResult | null>(null);
  const [abortController, setAbortController] = useState<AbortController | null>(null);
  const nfcUnsubscribeRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    const checkNfcSupport = async () => {
      // In Capacitor app, use native NFC plugin
      if (isCapacitor) {
        try {
          const { supported } = await NFC.isSupported();
          setIsSupported(supported);
          if (!supported) {
            logger.log('NFC no soportado en este dispositivo');
          }
        } catch (error) {
          logger.error('Error checking NFC support:', error);
          setIsSupported(false);
        }
      } else {
        // In browser, check for Web NFC API
        if ('NDEFReader' in window) {
          setIsSupported(true);
        } else {
          setIsSupported(false);
          logger.log('Web NFC is not supported on this device/browser');
        }
      }
    };

    checkNfcSupport();
  }, []);

  const triggerHapticFeedback = async (type: 'success' | 'error' | 'warning') => {
    try {
      switch (type) {
        case 'success':
          await Haptics.impact({ style: ImpactStyle.Medium });
          setTimeout(() => Haptics.impact({ style: ImpactStyle.Light }), 100);
          break;
        case 'error':
          await Haptics.impact({ style: ImpactStyle.Heavy });
          setTimeout(() => Haptics.impact({ style: ImpactStyle.Heavy }), 150);
          break;
        case 'warning':
          await Haptics.impact({ style: ImpactStyle.Light });
          break;
      }
    } catch (e) {
      // Haptics not available, fail silently
      logger.log('Haptics not available');
    }
  };

  const startScanning = useCallback(async () => {
    if (!isSupported) {
      await triggerHapticFeedback('error');
      toast.error("NFC no está soportado en este dispositivo o navegador");
      return;
    }

    try {
      setIsScanning(true);
      await triggerHapticFeedback('warning');
      toast.info("📱 Acerca tu dispositivo a una etiqueta NFC...", {
        duration: 3000,
        icon: "🔍"
      });

      // Use Capacitor NFC plugin for native app
      if (isCapacitor) {
        // Register error listener
        const unsubError = NFC.onError((error) => {
          logger.error("NFC Error:", error);
          triggerHapticFeedback('error');
          toast.error("❌ Error NFC: " + error.error);
        });

        // Register read listener
        const unsubRead = NFC.onRead(async (data) => {
          logger.log("NFC tag detected (Capacitor):", data);
          await triggerHapticFeedback('success');

          // El plugin puede devolver data en diferentes formatos
          let tagData: any;
          try {
            tagData = typeof data.string === 'function' ? data.string() : data;
          } catch {
            tagData = data;
          }

          // Intentar extraer el UID de múltiples ubicaciones posibles
          const serialNumber =
            tagData?.tagInfo?.uid ||
            tagData?.tag?.id ||
            tagData?.id ||
            tagData?.uid ||
            tagData?.serialNumber ||
            (data as any)?.tag?.id ||
            (data as any)?.tagInfo?.uid ||
            (data as any)?.id ||
            'unknown';

          logger.log('NFC startScanning serialNumber extracted:', serialNumber);

          const records = tagData?.messages?.[0]?.records || [];

          let parsedData: any = null;
          let recordType = "unknown";

          if (records.length > 0) {
            const record = records[0];
            recordType = record.type || "text";
            try {
              // Try to parse as JSON
              if (typeof record.payload === 'string') {
                parsedData = JSON.parse(record.payload);
              }
            } catch {
              // Not JSON, use raw payload
              parsedData = { raw: record.payload };
            }
          }

          setLastRead({
            serialNumber,
            data: parsedData,
            recordType,
            timestamp: Date.now(),
          });

          toast.success("✅ ¡Etiqueta NFC leída exitosamente!", {
            duration: 2000,
            icon: "🎉"
          });
        });

        // Store unsubscribe functions
        nfcUnsubscribeRef.current = () => {
          unsubRead();
          unsubError();
        };

        // Start scan - iOS needs this, Android uses foreground dispatch automatically
        try {
          await NFC.startScan();
        } catch (e: any) {
          // Android throws "Android NFC scanning does not require startScan" - this is normal
          if (!e.message?.includes('Android')) {
            throw e;
          }
          logger.log('Android NFC: usando foreground dispatch automático');
        }

      } else {
        // Use Web NFC API for browser
        const controller = new AbortController();
        setAbortController(controller);

        // @ts-ignore - NDEFReader is not yet in TypeScript types
        const ndef = new NDEFReader();

        // @ts-ignore
        await ndef.scan({ signal: controller.signal });

        // @ts-ignore
        ndef.onreading = async (event: any) => {
          logger.log("NFC tag detected:", event);
          await triggerHapticFeedback('success');

          const serialNumber = event.serialNumber;
          const records = event.message.records;

          let parsedData: any = null;
          let recordType = "unknown";

          if (records && records.length > 0) {
            const record = records[0];
            recordType = record.recordType;

            try {
              if (record.recordType === "text") {
                const textDecoder = new TextDecoder(record.encoding || "utf-8");
                const text = textDecoder.decode(record.data);
                parsedData = JSON.parse(text);
              } else if (record.recordType === "url") {
                const textDecoder = new TextDecoder("utf-8");
                parsedData = { url: textDecoder.decode(record.data) };
              } else if (record.recordType === "application/json") {
                const textDecoder = new TextDecoder("utf-8");
                const text = textDecoder.decode(record.data);
                parsedData = JSON.parse(text);
              }
            } catch (error) {
              logger.error("Error parsing NFC data:", error);
              await triggerHapticFeedback('error');
            }
          }

          setLastRead({
            serialNumber,
            data: parsedData,
            recordType,
            timestamp: Date.now(),
          });

          toast.success("✅ ¡Etiqueta NFC leída exitosamente!", {
            duration: 2000,
            icon: "🎉"
          });
        };

        // @ts-ignore
        ndef.onreadingerror = async (event: any) => {
          logger.error("NFC reading error:", event);
          await triggerHapticFeedback('error');
          toast.error("❌ Error al leer la etiqueta NFC");
        };
      }

    } catch (error: any) {
      logger.error("NFC Error:", error);
      await triggerHapticFeedback('error');

      if (error.name === 'NotAllowedError') {
        toast.error("🔒 Permiso de NFC denegado. Por favor permite el acceso NFC.");
      } else if (error.name === 'NotSupportedError') {
        toast.error("📵 NFC no está soportado en este dispositivo.");
      } else {
        toast.error("⚠️ Error al iniciar escaneo NFC: " + error.message);
      }

      setIsScanning(false);
    }
  }, [isSupported]);

  const stopScanning = useCallback(async (options?: { silent?: boolean }) => {
    if (isCapacitor) {
      // Cancel Capacitor NFC scan
      if (nfcUnsubscribeRef.current) {
        nfcUnsubscribeRef.current();
        nfcUnsubscribeRef.current = null;
      }
      try {
        await NFC.cancelScan();
      } catch {
        // cancelScan may not be supported on all platforms
      }
    } else {
      // Cancel Web NFC scan
      if (abortController) {
        abortController.abort();
        setAbortController(null);
      }
    }
    setIsScanning(false);
    // Solo mostrar toast si no es silencioso (ej: cuando el usuario cancela manualmente)
    if (!options?.silent) {
      await triggerHapticFeedback('warning');
      toast.info("⏸️ Escaneo NFC detenido");
    }
  }, [abortController]);

  const writeToTag = useCallback(async (data: NFCWritePayload) => {
    if (!isSupported) {
      await triggerHapticFeedback('error');
      toast.error("📵 NFC no está soportado en este dispositivo");
      return;
    }

    try {
      setIsWriting(true);

      const payload = Object.fromEntries(
        Object.entries(data).filter(([, value]) => value !== undefined && value !== null && value !== "")
      );
      const jsonData = JSON.stringify(payload);

      await triggerHapticFeedback('warning');
      toast.info("✍️ Acerca tu dispositivo a una etiqueta NFC para escribir...", {
        duration: 3000,
        icon: "📝"
      });

      if (isCapacitor) {
        // Use Capacitor NFC plugin
        const unsubWrite = NFC.onWrite(() => {
          triggerHapticFeedback('success');
          toast.success("✅ ¡Datos escritos en la etiqueta NFC exitosamente!", {
            duration: 2000,
            icon: "🎉"
          });
          setIsWriting(false);
        });

        const unsubError = NFC.onError((error) => {
          logger.error("NFC Write Error:", error);
          triggerHapticFeedback('error');
          toast.error("❌ Error al escribir: " + error.error);
          setIsWriting(false);
        });

        // Convert string to Uint8Array for the plugin
        const encoder = new TextEncoder();
        const payloadBytes = encoder.encode(jsonData);

        await NFC.writeNDEF({
          records: [
            {
              type: "T", // Text record
              payload: payloadBytes,
            },
          ],
        });

        // Cleanup listeners after a delay
        setTimeout(() => {
          unsubWrite();
          unsubError();
        }, 5000);

      } else {
        // Use Web NFC API
        // @ts-ignore
        const ndef = new NDEFReader();

        // @ts-ignore
        await ndef.write({
          records: [
            {
              recordType: "text",
              data: jsonData,
            },
          ],
        });

        await triggerHapticFeedback('success');
        toast.success("✅ ¡Datos escritos en la etiqueta NFC exitosamente!", {
          duration: 2000,
          icon: "🎉"
        });
        setIsWriting(false);
      }
    } catch (error: any) {
      logger.error("NFC Write Error:", error);
      await triggerHapticFeedback('error');

      if (error.name === 'NotAllowedError') {
        toast.error("🔒 Permiso de NFC denegado.");
      } else if (error.name === 'NotSupportedError') {
        toast.error("📵 Escritura NFC no soportada.");
      } else if (error.name === 'NotReadableError') {
        toast.error("🔐 La etiqueta NFC no es escribible o está protegida.");
      } else {
        toast.error("⚠️ Error al escribir en la etiqueta NFC: " + error.message);
      }
      setIsWriting(false);
    }
  }, [isSupported]);

  // Scan a tag to get its UID (especially for blank tags)
  const scanTagUid = useCallback(async (): Promise<NFCScanResult | null> => {
    if (!isSupported) {
      await triggerHapticFeedback('error');
      toast.error("📵 NFC no está soportado en este dispositivo");
      return null;
    }

    return new Promise(async (resolve) => {
      try {
        setIsScanning(true);

        await triggerHapticFeedback('warning');
        toast.info("📱 Acerca el chip NFC en blanco al dispositivo...", {
          duration: 5000,
          icon: "🔍"
        });

        if (isCapacitor) {
          // Use Capacitor NFC plugin
          let timeoutId: NodeJS.Timeout;
          let resolved = false;

          const unsubRead = NFC.onRead(async (data) => {
            if (resolved) return;
            resolved = true;
            clearTimeout(timeoutId);
            unsubRead();
            setIsScanning(false);

            // El plugin puede devolver data en diferentes formatos
            let tagData: any;
            try {
              tagData = typeof data.string === 'function' ? data.string() : data;
            } catch {
              tagData = data;
            }

            // Intentar extraer el UID de múltiples ubicaciones posibles
            const serialNumber =
              tagData?.tagInfo?.uid ||
              tagData?.tag?.id ||
              tagData?.id ||
              tagData?.uid ||
              tagData?.serialNumber ||
              (data as any)?.tag?.id ||
              (data as any)?.tagInfo?.uid ||
              (data as any)?.id ||
              'unknown';

            logger.log('NFC scanTagUid raw data:', JSON.stringify(data));
            logger.log('NFC scanTagUid serialNumber:', serialNumber);

            const records = tagData?.messages?.[0]?.records || [];
            const isEmpty = records.length === 0;

            setLastRead({
              serialNumber,
              data: null,
              recordType: isEmpty ? 'empty' : 'unknown',
              timestamp: Date.now(),
            });

            await triggerHapticFeedback('success');
            toast.success(`✅ Chip detectado: ${serialNumber}`, {
              duration: 3000,
              icon: "🎉"
            });

            resolve({ serialNumber, isEmpty });
          });

          // Start scan - iOS needs this, Android uses foreground dispatch automatically
          try {
            await NFC.startScan();
          } catch (e: any) {
            // Android throws error - this is normal, foreground dispatch is automatic
            if (!e.message?.includes('Android')) {
              throw e;
            }
          }

          // Timeout after 30 seconds
          timeoutId = setTimeout(async () => {
            if (resolved) return;
            resolved = true;
            unsubRead();
            try { await NFC.cancelScan(); } catch {}
            setIsScanning(false);
            toast.info("⏱️ Tiempo de escaneo agotado");
            resolve(null);
          }, 30000);

        } else {
          // Use Web NFC API
          // @ts-ignore
          const ndef = new NDEFReader();
          const controller = new AbortController();

          // @ts-ignore
          await ndef.scan({ signal: controller.signal });

          // @ts-ignore
          ndef.onreading = async (event: any) => {
            controller.abort();
            setIsScanning(false);

            const serialNumber = event.serialNumber;
            const records = event.message?.records || [];
            const isEmpty = records.length === 0;

            setLastRead({
              serialNumber,
              data: null,
              recordType: isEmpty ? 'empty' : 'unknown',
              timestamp: Date.now(),
            });

            await triggerHapticFeedback('success');
            toast.success(`✅ Chip detectado: ${serialNumber}`, {
              duration: 3000,
              icon: "🎉"
            });

            resolve({ serialNumber, isEmpty });
          };

          // @ts-ignore
          ndef.onreadingerror = async () => {
            controller.abort();
            setIsScanning(false);
            await triggerHapticFeedback('error');
            toast.error("❌ Error al leer el chip NFC");
            resolve(null);
          };

          // Timeout after 30 seconds
          setTimeout(() => {
            controller.abort();
            setIsScanning(false);
            toast.info("⏱️ Tiempo de escaneo agotado");
            resolve(null);
          }, 30000);
        }

      } catch (error: any) {
        setIsScanning(false);
        logger.error("NFC Scan Error:", error);
        await triggerHapticFeedback('error');
        toast.error("⚠️ Error al escanear: " + error.message);
        resolve(null);
      }
    });
  }, [isSupported]);

  // Write full data to a tag (for blank tags or overwriting)
  const writeFullTag = useCallback(async (uid: string, data: NFCWritePayload): Promise<boolean> => {
    if (!isSupported) {
      await triggerHapticFeedback('error');
      toast.error("📵 NFC no está soportado en este dispositivo");
      return false;
    }

    try {
      setIsWriting(true);

      // Include the UID in the payload
      const payload = {
        ...Object.fromEntries(
          Object.entries(data).filter(([, value]) => value !== undefined && value !== null && value !== "")
        ),
        tagUid: uid,
        timestamp: new Date().toISOString(),
      };
      const jsonData = JSON.stringify(payload);

      await triggerHapticFeedback('warning');
      toast.info("✍️ Acerca el chip NFC para grabar los datos...", {
        duration: 5000,
        icon: "📝"
      });

      if (isCapacitor) {
        // Use Capacitor NFC plugin
        return new Promise((resolve) => {
          const unsubWrite = NFC.onWrite(() => {
            unsubWrite();
            unsubError();
            triggerHapticFeedback('success');
            toast.success("✅ ¡Chip NFC grabado exitosamente!", {
              duration: 2000,
              icon: "🎉"
            });
            setIsWriting(false);
            resolve(true);
          });

          const unsubError = NFC.onError((error) => {
            unsubWrite();
            unsubError();
            logger.error("NFC Write Full Error:", error);
            triggerHapticFeedback('error');
            toast.error("❌ Error al grabar: " + error.error);
            setIsWriting(false);
            resolve(false);
          });

          const encoder = new TextEncoder();
          const payloadBytes = encoder.encode(jsonData);

          NFC.writeNDEF({
            records: [
              {
                type: "T",
                payload: payloadBytes,
              },
            ],
          }).catch((error) => {
            unsubWrite();
            unsubError();
            logger.error("NFC writeNDEF error:", error);
            triggerHapticFeedback('error');
            toast.error("⚠️ Error al grabar el chip: " + error.message);
            setIsWriting(false);
            resolve(false);
          });
        });

      } else {
        // Use Web NFC API
        // @ts-ignore
        const ndef = new NDEFReader();

        // @ts-ignore
        await ndef.write({
          records: [
            {
              recordType: "text",
              data: jsonData,
            },
          ],
        });

        await triggerHapticFeedback('success');
        toast.success("✅ ¡Chip NFC grabado exitosamente!", {
          duration: 2000,
          icon: "🎉"
        });
        return true;
      }
    } catch (error: any) {
      logger.error("NFC Write Full Error:", error);
      await triggerHapticFeedback('error');

      if (error.name === 'NotAllowedError') {
        toast.error("🔒 Permiso de NFC denegado.");
      } else if (error.name === 'NotReadableError') {
        toast.error("🔐 El chip NFC no es escribible o está protegido.");
      } else {
        toast.error("⚠️ Error al grabar el chip: " + error.message);
      }
      return false;
    } finally {
      setIsWriting(false);
    }
  }, [isSupported]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortController) {
        abortController.abort();
      }
      if (nfcUnsubscribeRef.current) {
        nfcUnsubscribeRef.current();
        nfcUnsubscribeRef.current = null;
      }
    };
  }, [abortController]);

  return {
    isScanning,
    isWriting,
    isSupported,
    lastRead,
    startScanning,
    stopScanning,
    writeToTag,
    scanTagUid,
    writeFullTag,
  };
};
